import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { BehaviorSubject, combineLatest, Observable, scan, Subject } from 'rxjs'
import {
  DataContextBuilder,
  Filter,
  IDataContext,
  Quantity,
  Sort,
  Unit,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderDataToolbarComponent,
  ElderToolbarContentDirective,
  ElderNumberCellDirective,
  ElderQuantityPipe,
} from '@elderbyte/ngx-starter'
import { WorkItem, WorkItemState } from '../../../work-items/work-item'
import { WorkItemFilter, WorkItemService } from '../../../work-items/work-item.service'
import { map, takeUntil } from 'rxjs/operators'
import {
  IWorkItemDetailDialogOptions,
  WorkItemDetailDialogComponent,
} from '../../../work-items/work-item-detail-dialog/work-item-detail-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import { ProcessPlan } from '../../../../process/process-plans/process-plan'
import { ProcessPlanService } from '../../../../process/process-plans/process-plan.service'
import { ConstraintInput } from '../../../../shopfloor/workcenters/capability/constraints/constraintInput'
import {
  buildConstraint,
  Constraint,
} from '../../../../shopfloor/workcenters/capability/constraints/constraint'
import { ProductSalesQuantityPipe } from '../../../../../features/pipes/product-sales-quantity.pipe'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { ItemManufacturingStatusComponent } from '../../../work-items/item-manufacturing-status/item-manufacturing-status.component'
import {
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
} from '@angular/material/table'
import { WorkItemFilterComponent } from '../../../work-orders/work-item-filter-component/work-item-filter.component'
import { AsyncPipe } from '@angular/common'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-work-item-utilisation-table',
  templateUrl: './work-item-utilisation-table.component.html',
  styleUrls: ['./work-item-utilisation-table.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    WorkItemFilterComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    ElderNumberCellDirective,
    ItemManufacturingStatusComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ProductSalesQuantityPipe,
    ElderQuantityPipe,
  ],
})
export class WorkItemUtilisationTableComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly workCenterId$ = new BehaviorSubject<string>(null)
  public readonly workOrderId$ = new BehaviorSubject<string>(null)
  public readonly itemConstraints$ = new BehaviorSubject<ConstraintInput[]>([])

  public readonly enableSelection = input<boolean>(false)
  public readonly refreshContext$ = new BehaviorSubject<void>(null)

  public readonly data: IDataContext<WorkItem>

  public readonly Unit = Unit

  public initialFilter = input<Filter>(null)

  @Output()
  public selectionChanged: EventEmitter<WorkItem[]> = new EventEmitter<WorkItem[]>()

  @Output()
  public readonly canChangeRoutes: EventEmitter<boolean> = new EventEmitter<boolean>()
  public readonly involvedProcessPlans$: Observable<ProcessPlan[]>

  private matDialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const processPlanService = inject(ProcessPlanService)
    const workItemService = inject(WorkItemService)
    this.data = DataContextBuilder.start<WorkItem>().pageSize(100).build(workItemService)
    const involvedProcessPlanIds$ = this.data.data
      .pipe(map((items) => items.map((item) => item.processPlanId)))
      .pipe(scan((acc, value) => [...acc, ...value]))

    this.involvedProcessPlans$ = processPlanService.fetchByIds$(involvedProcessPlanIds$)

    this.data.data.subscribe((items) => {
      this.canChangeRoutes.emit(items.some((item) => item.processingState === WorkItemState.OPEN))
    })

    combineLatest([
      this.workOrderId$,
      this.workCenterId$,
      this.itemConstraints$,
      this.refreshContext$,
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([workOrderId, workCenterId, constraints]) => {
        if (workOrderId && workCenterId) {
          this.data.start(
            [new Sort('number', 'asc')],
            this.buildFilters(workOrderId, workCenterId, constraints, this.initialFilter())
          )
        }
      })
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set workCenterId(id: string) {
    this.workCenterId$.next(id)
  }

  @Input()
  public set workOrderId(id: string) {
    this.workOrderId$.next(id)
  }

  @Input()
  public set itemsToReload(ids: string[]) {
    this.data.reload()
  }

  @Input()
  public set itemConstraints(constraints: ConstraintInput[]) {
    this.itemConstraints$.next(constraints)
  }

  @Input()
  public set refreshContext(_: any) {
    this.refreshContext$.next()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onSelectionChange($event: WorkItem[]): void {
    this.selectionChanged.emit($event)
  }

  public openDetail(item: WorkItem): void {
    this.matDialog.open(WorkItemDetailDialogComponent, {
      maxWidth: '95vw',
      maxHeight: '95vh',
      width: '95vw',
      height: '95vh',
      data: <IWorkItemDetailDialogOptions>{
        item: item,
      },
    })
  }

  protected toQuantity(length: number | undefined, unit: Unit): Quantity {
    if (!!length && !!unit) {
      return Quantity.from(unit, length)
    } else {
      return Quantity.zero(Unit.cm)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private buildFilters(
    workOrderId: string,
    workCenterId: string,
    constraints: ConstraintInput[],
    initialFilter: Filter
  ): Filter[] {
    const itemFilters: Filter[] = [
      WorkItemFilter.workOrderId(workOrderId),
      WorkItemFilter.workCenterId(workCenterId),
      WorkItemFilter.constraints(
        constraints.map((constraintInput) => buildConstraint(constraintInput))
      ),
    ]

    if (!!initialFilter) {
      itemFilters.push(initialFilter)
    }

    return itemFilters
  }
}
